import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@hooks/reduxHooks';
import { $api } from '@service/RequestService';
import { isEmptyArr } from '@utils/index';
import { useQuery } from 'react-query';
import { NumberParam, useQueryParam } from 'use-query-params';

import { queryKeys } from '../constants';
import { DashboardModel } from '../models';
import { setActiveDashboard, setRedirectDashboard } from '../store';

export function useDashboardId(isAccessDashboards?: boolean) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isDashboardPage = pathname?.includes('dashboard');
  const user = useAppSelector(state => state.userReducer.user);
  const [currentId, setCurrentId] = useQueryParam('dashboardId', NumberParam);

  useQuery({
    queryKey: ['table-dashboard-id'],
    queryFn: async () => {
      const { data } = await $api.get('/v1/table/dashboard-id');

      return Number(data?.data);
    },
    select: data => {
      if (currentId !== data && isDashboardPage) {
        setCurrentId(data);
      }

      return data;
    },
    enabled: isDashboardPage
  });

  const { data, ...arg } = useQuery<{
    dashboardId: number;
    dashboards: DashboardModel[];
  }>(
    [queryKeys.DASHBOARD_ID, currentId],
    async () => {
      const dashboardId = Number(currentId);
      const { data: dashboards } = await $api.get('/v1/dashboard');

      return { dashboardId, dashboards: dashboards?.data };
    },
    {
      onSuccess: ({ dashboardId: resDashboardId, dashboards }) => {
        if (isDashboardPage) {
          const dashboardId = currentId || resDashboardId;
          let activeDashboard: DashboardModel | undefined;
          let redirectDashboard: DashboardModel | undefined;
          const isNotDashboard = !dashboards?.some(({ id }) => id === dashboardId);

          dashboards?.forEach(({ id, ...arg }) => {
            if (id === dashboardId) {
              activeDashboard = { id, ...arg };
            }

            if (!redirectDashboard && id !== dashboardId) {
              redirectDashboard = { id, ...arg };
            }
          });

          if (isNotDashboard && isDashboardPage) {
            setCurrentId(dashboards?.[0]?.id);

            activeDashboard = { ...(dashboards?.[0] || {}) };

            if (dashboards?.[1]) {
              redirectDashboard = { ...(dashboards?.[1] || {}) };
            }
          }

          dispatch(setActiveDashboard(activeDashboard));
          dispatch(setRedirectDashboard(redirectDashboard));
        }
      },
      select: ({ dashboardId, dashboards }) => ({
        dashboardId,
        dashboards: isAccessDashboards
          ? dashboards?.filter(({ dashboardUsersDto, ownerId }) =>
              ownerId === Number(user?.id) || isEmptyArr(dashboardUsersDto)
                ? true
                : dashboardUsersDto?.find(({ employeeId }) => Number(employeeId) === Number(user?.id))?.permission ===
                  'EDIT'
            )
          : dashboards
      })
    }
  );

  return { ...data, ...arg };
}
